import type { CaptionModelStatus } from '@/lib/utils/tauri-recording'
import { useEffect, useState } from 'react'
import { isTauri } from '@/lib/utils/tauri'
import {
  downloadCaptionModel,
  getCaptionModelStatus,
  listenForCaptionModelProgress,
} from '@/lib/utils/tauri-recording'
import { toast } from 'sonner'

/**
 * The on-device caption model (desktop only): its status, the one-time
 * download's progress, and `ensureReady`, which downloads it on first use.
 * Shared by the record menu's preference checkbox and the in-meeting switch.
 */
export function useCaptionModel() {
  const [model, setModel] = useState<CaptionModelStatus | null>(null)
  const [downloadPct, setDownloadPct] = useState<number | null>(null)

  useEffect(() => {
    if (isTauri()) void getCaptionModelStatus().then(setModel)
  }, [])

  const ensureReady = async (): Promise<boolean> => {
    if (model?.state === 'ready') return true
    if (model?.state === 'downloading' || downloadPct !== null) return false
    setDownloadPct(0)
    const unlisten = await listenForCaptionModelProgress(progress => {
      if (progress.totalBytes > 0) {
        setDownloadPct(
          Math.round((progress.downloadedBytes / progress.totalBytes) * 100),
        )
      }
    })
    try {
      await downloadCaptionModel()
      setModel({ supported: true, state: 'ready' })
      toast.success('Live captions ready')
      return true
    } catch (error) {
      toast.error('Caption model download failed', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 160),
      })
      return false
    } finally {
      unlisten()
      setDownloadPct(null)
    }
  }

  return { model, downloadPct, ensureReady }
}
